import * as React from "react";
import { Link } from "@nextui-org/link";
import { Input } from "@nextui-org/input";
import { Avatar } from "@nextui-org/avatar";
import { CircularProgress } from "@nextui-org/progress";
import { Select, SelectItem } from "@nextui-org/select";
import { button as buttonStyles } from "@nextui-org/theme";
import { Card, CardBody, CardFooter } from "@nextui-org/card";

import { LineItem } from "@/components/line-item";
import { ApiResponseInterface, CurrencyProps } from "@/interfaces";
import useApi from "@/src/hooks/useApi";
import { siteConfig } from "@/config/site";
import { makeRequest } from "@/src/helpers/request";

export const Transfer = () => {
  // Get currencies
  const {
    data: currencyData,
    error: currencyError,
    loaded: currencyLoaded,
  } = useApi("/api/external/get-currencies", "GET", {}, true);
  // Get chains
  const {
    data: chainsData,
    error: chainsError,
    loaded: chainsLoaded,
  } = useApi("/api/external/get-chains-mixed", "GET", {}, true);

  const [apiLoading, setApiLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [wallet, setWallet] = React.useState("");
  const [previewData, setPreviewData] = React.useState({
    chain: "usdt:trc20",
    amount: "1000",
    total_fees: 0,
    sending_amount: "0",
    rate: 0,
    receiving_amount: "0",
    currency: "CNY",
    receiving_currency: "CNY",
    arrival: "",
  });

  const checkAndFetchCharges = async () => {
    // Set API loading state
    setApiLoading(true);
    // set wallet
    const walletSplit = previewData.chain.split(":");

    setWallet(walletSplit[0].toUpperCase());

    const data = {
      amount: previewData.amount,
      currency: previewData.currency,
      chain: previewData.chain,
    };
    const chargesResponse: ApiResponseInterface = await makeRequest(
      "/api/external/get-charges",
      "POST",
      data,
      true,
    );

    if (chargesResponse.status == true) {
      const resp = chargesResponse.data.response;

      // set the data
      setPreviewData({
        ...previewData,
        total_fees: resp.total_charges,
        sending_amount: resp.sending_amount,
        receiving_amount: resp.receiving_amount,
        receiving_currency: resp.receiving_currency,
        rate: resp.rate,
        arrival: "Should arrive in 2hrs",
      });
      // Set success
      setSuccess(true);
      setApiLoading(false);
    }
  };

  // On page load, after 5seconds, fetch the charges for default selected values
  // Fetch once
  React.useEffect(() => {
    async () => {
      await checkAndFetchCharges();
    };
    const timer = setTimeout(() => {
      checkAndFetchCharges();
    }, 2000);

    return () => clearTimeout(timer);
  }, [previewData.amount, previewData.chain, previewData.currency]);

  // Handle field changes
  const handleOnChange = (e: any) => {
    setPreviewData({ ...previewData, [e.target.name]: e.target.value });
  };

  return (
    <div className="ct-transfer-card w-5/6" data-aos="slide-up">
      <Card>
        <CardBody>
          {chainsError && (<div className="">{chainsError.message}</div>)}
          {currencyError && (<div className="">{currencyError.message}</div>)}
          <div className="ct-tx-card mt-8  md:w-full">
            <div className="mt-3 flex flex-row gap-6 justify-center items-center">
              <Input
                isRequired
                className="max-w-xs"
                fullWidth={true}
                label="Coin Amount"
                name="amount"
                placeholder="0.00"
                size="lg"
                type="number"
                value={previewData.amount}
                onChange={handleOnChange}
                labelPlacement="outside"
                // description="The coin amount you're sending."
              />

              {chainsLoaded ? (
                <Select
                  isRequired
                  className="max-w-xs"
                  defaultSelectedKeys={[previewData.chain]}
                  fullWidth={true}
                  items={chainsData.response ?? []}
                  label="Wallet"
                  labelPlacement="outside"
                  name="chain"
                  placeholder="select"
                  size="lg"
                  onChange={handleOnChange}
                >
                  {(wallet: { code: string; label: string }) => (
                    <SelectItem key={wallet.code}>{wallet.label}</SelectItem>
                  )}
                </Select>
              ) : (
                <CircularProgress aria-label="Loading..." size="sm" />
              )}
            </div>

            {success && (
              <div className="ct-line-items mt-8">
                <LineItem
                  icon="mdi:minus"
                  isLeftIconBig={true}
                  isRightBold={false}
                  leftText={`${previewData.total_fees} ${wallet}`}
                  rightText="Total fees"
                />
                <LineItem
                  icon="mdi:equal"
                  isLeftIconBig={true}
                  isRightBold={false}
                  leftText={`${previewData.sending_amount} ${wallet}`}
                  rightText="Total amount we'll convert"
                />
                <LineItem
                  icon="mdi:times"
                  isLeftIconBig={true}
                  isRightBold={true}
                  leftText={`${previewData.rate} ${previewData.receiving_currency}`}
                  rightText="Guranteed Rate (8h)"
                />
              </div>
            )}

            <div className="ml-1 mb-3 mt-8" />
            <div className="flex flex-row gap-6 justify-center items-center">
              <Input
                isReadOnly
                aria-label="Receiving amount"
                className="max-w-xs"
                fullWidth={true}
                label="Receiving Amount"
                labelPlacement="outside"
                name="receiving_amount"
                placeholder="0.00"
                size="lg"
                type="number"
                value={previewData.receiving_amount}
              />

              {currencyLoaded ? (
                <Select
                  isRequired
                  aria-label="Currency Select"
                  className="max-w-xs"
                  defaultSelectedKeys={[previewData.currency]}
                  fullWidth={true}
                  items={currencyData.response}
                  label="Destination Currency"
                  labelPlacement="outside"
                  name="currency"
                  placeholder="select"
                  size="lg"
                  onChange={handleOnChange}
                >
                  {(currency: CurrencyProps) => (
                    <SelectItem
                      key={currency.code}
                      startContent={
                        <Avatar
                          alt={currency.code}
                          className="flex-shrink-0"
                          size="sm"
                          src={
                            `${import.meta.env.VITE_APP_BACKEND_URL}` +
                            currency.flag
                          }
                        />
                      }
                    >{`${currency.name} (${currency.code})`}</SelectItem>
                  )}
                </Select>
              ) : (
                <CircularProgress aria-label="Loading..." size="sm" />
              )}
            </div>
          </div>
        </CardBody>
        <CardFooter className="flex flex-col justify-between items-center text-center">
          {apiLoading && <CircularProgress aria-label="Loading..." size="sm" />}
          <p className="mb-4">{previewData.arrival}</p>
          <Link
            isExternal
            className={buttonStyles({
              color: "default",
              radius: "full",
              fullWidth: true,
              variant: "shadow",
            })}
            href={siteConfig.links.transfer}
          >
            <span className="text-white">Send money now</span>
          </Link>
          <div className="mb-4" />
        </CardFooter>
      </Card>
    </div>
  );
};
